import { useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { GenevaRequest, CreateGenevaRequestData, GenevaRequestStatus, GenevaRequestAction, BulkGenevaRequest, CreateBulkGenevaRequestData } from '../../../types/geneva-schema';
import { SchemaType } from '../../../types/schema';
import { DEFAULT_SCHEMA_TYPE, SCHEMA_CONFIG } from '../../../utils/schema-config';
import { useFirestore } from '../../../helpers/firebaseContext';

interface UseGenevaRequestServiceProps {
  orgId: string;
  userId: string;
  userEmail: string;
  userDisplayName?: string;
  schemaType?: SchemaType;
}

export const useGenevaRequestService = ({ orgId, userId, userEmail, userDisplayName, schemaType = DEFAULT_SCHEMA_TYPE }: UseGenevaRequestServiceProps) => {

  const db = useFirestore();
  const requestCollectionName = SCHEMA_CONFIG[schemaType].requestCollection;
  const bulkRequestCollectionName = SCHEMA_CONFIG[schemaType].bulkRequestCollection;


  // Collection reference
  const getRequestsCollection = useCallback(() => {
    return collection(db, requestCollectionName);
  }, [db, requestCollectionName]);

  // Create a new Geneva request
  const createRequest = useCallback(async (data: CreateGenevaRequestData): Promise<string> => {
    const requestsCollection = getRequestsCollection();

    const requestDoc: Omit<GenevaRequest, 'id'> = {
      orgId: data.orgId,
      templateId: data.templateId,
      schemaType: data.schemaType ?? schemaType,
      status: 'draft',
      formData: data.formData,
      metadata: data.metadata,
      audit: {
        createdAt: Date.now(),
        createdBy: userId,
        createdByEmail: userEmail,
        createdByName: userDisplayName,
      },
    };

    if (data.supportingData !== undefined) {
      (requestDoc as any).supportingData = data.supportingData;
    }

    console.log('requestDoc', requestDoc);
    const docRef = await addDoc(requestsCollection, {
      ...requestDoc,
      createdAt: serverTimestamp(),
    });

    return docRef.id;
  }, [getRequestsCollection, userId, userEmail, userDisplayName, schemaType]);

  // Update an existing request
  const updateRequest = useCallback(async (requestId: string, updates: Partial<GenevaRequest>): Promise<void> => {
    const requestDoc = doc(db, requestCollectionName, requestId);

    await updateDoc(requestDoc, {
      ...updates,
      'audit.updatedAt': Date.now(),
      'audit.updatedBy': userId,
      'audit.updatedByEmail': userEmail,
      'audit.updatedByName': userDisplayName,
      updatedAt: serverTimestamp(),
    });
  }, [db, requestCollectionName, userId, userEmail, userDisplayName]);

  // Update request form data
  const updateRequestData = useCallback(async (
    requestId: string,
    formData: Record<string, any>,
    supportingData?: Record<string, any>
  ): Promise<void> => {
    const requestDoc = doc(db, requestCollectionName, requestId);
    const updates: Record<string, any> = {
      formData,
      'audit.updatedAt': Date.now(),
      'audit.updatedBy': userId,
      'audit.updatedByEmail': userEmail,
      'audit.updatedByName': userDisplayName,
      updatedAt: serverTimestamp(),
    };

    if (supportingData !== undefined) {
      updates.supportingData = supportingData;
    }

    await updateDoc(requestDoc, updates);
  }, [db, requestCollectionName, userId, userEmail, userDisplayName]);

  // Submit request for approval
  const submitForApproval = useCallback(async (requestId: string): Promise<void> => {
    const requestDoc = doc(db, requestCollectionName, requestId);

    await updateDoc(requestDoc, {
      status: 'pending',
      'audit.updatedAt': Date.now(),
      'audit.updatedBy': userId,
      'audit.updatedByEmail': userEmail,
      'audit.updatedByName': userDisplayName,
      updatedAt: serverTimestamp(),
    });
  }, [db, requestCollectionName, userId, userEmail, userDisplayName]);

  // Approve a request
  const approveRequest = useCallback(async (requestId: string): Promise<void> => {
    const requestDoc = doc(db, requestCollectionName, requestId);

    await updateDoc(requestDoc, {
      status: 'approved',
      'audit.approvedAt': Date.now(),
      'audit.approvedBy': userId,
      'audit.approvedByEmail': userEmail,
      'audit.approvedByName': userDisplayName,
      'audit.updatedAt': Date.now(),
      'audit.updatedBy': userId,
      'audit.updatedByEmail': userEmail,
      'audit.updatedByName': userDisplayName,
      updatedAt: serverTimestamp(),
    });
  }, [db, requestCollectionName, userId, userEmail, userDisplayName]);

  // removed rejectRequest – no longer supported

  // Delete a request
  const deleteRequest = useCallback(async (requestId: string): Promise<void> => {
    const requestDoc = doc(db, requestCollectionName, requestId);
    await deleteDoc(requestDoc);
  }, [db, requestCollectionName]);

  // Get a single request
  const getRequest = useCallback(async (requestId: string): Promise<GenevaRequest | null> => {
    const requestDoc = doc(db, requestCollectionName, requestId);
    const docSnap = await getDoc(requestDoc);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GenevaRequest;
    }

    return null;
  }, [db, requestCollectionName]);

  // Get requests for an organization
  const getOrgRequests = useCallback(async (
    status?: GenevaRequestStatus,
    limitCount: number = 50
  ): Promise<GenevaRequest[]> => {
    const requestsCollection = getRequestsCollection();

    let q = query(
      requestsCollection,
      where('orgId', '==', orgId),
      orderBy('audit.createdAt', 'desc'),
      limit(limitCount)
    );

    if (status) {
      q = query(
        requestsCollection,
        where('orgId', '==', orgId),
        where('status', '==', status),
        orderBy('audit.createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as GenevaRequest[];
  }, [getRequestsCollection, orgId]);

  // Get user's requests
  const getUserRequests = useCallback(async (
    status?: GenevaRequestStatus,
    limitCount: number = 50
  ): Promise<GenevaRequest[]> => {
    const requestsCollection = getRequestsCollection();

    let q = query(
      requestsCollection,
      where('orgId', '==', orgId),
      where('audit.createdBy', '==', userId),
      orderBy('audit.createdAt', 'desc'),
      limit(limitCount)
    );

    if (status) {
      q = query(
        requestsCollection,
        where('orgId', '==', orgId),
        where('audit.createdBy', '==', userId),
        where('status', '==', status),
        orderBy('audit.createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as GenevaRequest[];
  }, [getRequestsCollection, orgId, userId]);

  // Get pending requests (for approval)
  const getPendingRequests = useCallback(async (limitCount: number = 50): Promise<GenevaRequest[]> => {
    return getOrgRequests('pending', limitCount);
  }, [getOrgRequests]);

  // --- BULK REQUEST METHODS ---

  // Collection reference for bulk requests
  const getBulkRequestsCollection = useCallback(() => {
    return collection(db, bulkRequestCollectionName);
  }, [db, bulkRequestCollectionName]);

  // Create a new bulk Geneva request
  const createBulkRequest = useCallback(async (data: CreateBulkGenevaRequestData): Promise<string> => {
    const bulkRequestsCollection = getBulkRequestsCollection();

    const bulkRequestDoc: Omit<BulkGenevaRequest, 'id'> = {
      orgId: data.orgId,
      templateId: data.templateId,
      schemaType: data.schemaType ?? schemaType,
      status: 'draft',
      isBulk: true,
      recordType: data.recordType,
      fieldMappings: Object.fromEntries(
        Object.entries(data.fieldMappings).map(([key, mapping]) => [key, {
          columnIndex: mapping.columnIndex,
          columnName: mapping.columnName,
          isRequired: mapping.isRequired,
          dataType: mapping.dataType,
        }])
      ),
      parsedData: {
        ...data.parsedData,
        rows: JSON.stringify(data.parsedData.rows), // Serialize nested array
      },
      bulkFormData: JSON.stringify(data.bulkFormData), // Serialize array of objects
      metadata: data.metadata,
      audit: {
        createdAt: Date.now(),
        createdBy: userId,
        createdByEmail: userEmail,
        createdByName: userDisplayName,
      },
    };

    const docRef = await addDoc(bulkRequestsCollection, {
      ...bulkRequestDoc,
      createdAt: serverTimestamp(),
    });

    return docRef.id;
  }, [getBulkRequestsCollection, userId, userEmail, userDisplayName, schemaType]);

  // Update an existing bulk request
  const updateBulkRequest = useCallback(async (requestId: string, updates: Partial<BulkGenevaRequest>): Promise<void> => {
    const requestDoc = doc(db, bulkRequestCollectionName, requestId);

    await updateDoc(requestDoc, {
      ...updates,
      'audit.updatedAt': Date.now(),
      'audit.updatedBy': userId,
      'audit.updatedByEmail': userEmail,
      'audit.updatedByName': userDisplayName,
      updatedAt: serverTimestamp(),
    });
  }, [db, bulkRequestCollectionName, userId, userEmail, userDisplayName]);

  // Submit bulk request for approval
  const submitBulkForApproval = useCallback(async (requestId: string): Promise<void> => {
    const requestDoc = doc(db, bulkRequestCollectionName, requestId);

    await updateDoc(requestDoc, {
      status: 'pending',
      'audit.updatedAt': Date.now(),
      'audit.updatedBy': userId,
      'audit.updatedByEmail': userEmail,
      'audit.updatedByName': userDisplayName,
      updatedAt: serverTimestamp(),
    });
  }, [db, bulkRequestCollectionName, userId, userEmail, userDisplayName]);

  // Approve a bulk request
  const approveBulkRequest = useCallback(async (requestId: string): Promise<void> => {
    const requestDoc = doc(db, bulkRequestCollectionName, requestId);

    await updateDoc(requestDoc, {
      status: 'approved',
      'audit.approvedAt': Date.now(),
      'audit.approvedBy': userId,
      'audit.approvedByEmail': userEmail,
      'audit.approvedByName': userDisplayName,
      'audit.updatedAt': Date.now(),
      'audit.updatedBy': userId,
      'audit.updatedByEmail': userEmail,
      'audit.updatedByName': userDisplayName,
      updatedAt: serverTimestamp(),
    });
  }, [db, bulkRequestCollectionName, userId, userEmail, userDisplayName]);

  // removed rejectBulkRequest – no longer supported

  // Delete a bulk request
  const deleteBulkRequest = useCallback(async (requestId: string): Promise<void> => {
    const requestDoc = doc(db, bulkRequestCollectionName, requestId);
    await deleteDoc(requestDoc);
  }, [db, bulkRequestCollectionName]);

  // Get a single bulk request
  const getBulkRequest = useCallback(async (requestId: string): Promise<BulkGenevaRequest | null> => {
    const requestDoc = doc(db, bulkRequestCollectionName, requestId);
    const docSnap = await getDoc(requestDoc);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        // Deserialize the JSON strings back to arrays
        parsedData: {
          ...data.parsedData,
          rows: JSON.parse(data.parsedData.rows),
        },
        bulkFormData: JSON.parse(data.bulkFormData) as Record<string, any>[],
      } as unknown as BulkGenevaRequest;
    }

    return null;
  }, [db, bulkRequestCollectionName]);

  // Get bulk requests for an organization
  const getOrgBulkRequests = useCallback(async (
    status?: GenevaRequestStatus,
    limitCount: number = 50
  ): Promise<BulkGenevaRequest[]> => {
    const bulkRequestsCollection = getBulkRequestsCollection();

    let q = query(
      bulkRequestsCollection,
      where('orgId', '==', orgId),
      orderBy('audit.createdAt', 'desc'),
      limit(limitCount)
    );

    if (status) {
      q = query(
        bulkRequestsCollection,
        where('orgId', '==', orgId),
        where('status', '==', status),
        orderBy('audit.createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Deserialize the JSON strings back to arrays
        parsedData: {
          ...data.parsedData,
          rows: JSON.parse(data.parsedData.rows),
        },
        bulkFormData: JSON.parse(data.bulkFormData) as Record<string, any>[],
      } as unknown as BulkGenevaRequest;
    });
  }, [getBulkRequestsCollection, orgId]);

  // Get user's bulk requests
  const getUserBulkRequests = useCallback(async (
    status?: GenevaRequestStatus,
    limitCount: number = 50
  ): Promise<BulkGenevaRequest[]> => {
    const bulkRequestsCollection = getBulkRequestsCollection();

    let q = query(
      bulkRequestsCollection,
      where('orgId', '==', orgId),
      where('audit.createdBy', '==', userId),
      orderBy('audit.createdAt', 'desc'),
      limit(limitCount)
    );

    if (status) {
      q = query(
        bulkRequestsCollection,
        where('orgId', '==', orgId),
        where('audit.createdBy', '==', userId),
        where('status', '==', status),
        orderBy('audit.createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Deserialize the JSON strings back to arrays
        parsedData: {
          ...data.parsedData,
          rows: JSON.parse(data.parsedData.rows),
        },
        bulkFormData: JSON.parse(data.bulkFormData) as Record<string, any>[],
      } as unknown as BulkGenevaRequest;
    });
  }, [getBulkRequestsCollection, orgId, userId]);

  // Get pending bulk requests (for approval)
  const getPendingBulkRequests = useCallback(async (limitCount: number = 50): Promise<BulkGenevaRequest[]> => {
    return getOrgBulkRequests('pending', limitCount);
  }, [getOrgBulkRequests]);

  return {
    // Regular request methods
    createRequest,
    updateRequest,
    updateRequestData,
    submitForApproval,
    approveRequest,
    deleteRequest,
    getRequest,
    getOrgRequests,
    getUserRequests,
    getPendingRequests,

    // Bulk request methods
    createBulkRequest,
    updateBulkRequest,
    submitBulkForApproval,
    approveBulkRequest,
    deleteBulkRequest,
    getBulkRequest,
    getOrgBulkRequests,
    getUserBulkRequests,
    getPendingBulkRequests,
  };
};
